import React, { useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { FeatureGate, isLive } from '../../components/platform/FeatureStatusBadge';
import { OrderStateBadge, OrderStateTrack } from '../../components/marketplace/OrderStateTrack';
import { providerEarnings, providerOrders, providerPackages, providerProfile } from '../../data/provider';
import { naira } from '../../utils/format';
import { ProviderServiceQueue } from '../../components/services/ProviderServiceQueue';
import { AttentionBoard } from '../../components/operator/AttentionBoard';
import { useSectionRoute } from '../../hooks/useSectionRoute';
const TABS = ['Today', 'Orders', 'Academic services', 'Services', 'Earnings', 'Reputation'] as const;
export function ProviderOverview() {
  const [tab, setTab] = useSectionRoute('/provider', TABS);
  const [openId, setOpenId] = useState(providerOrders[0].id);

  // What is genuinely waiting on the provider, derived from order state rather
  // than a stored counter.
  const awaiting = providerOrders.filter(order => ['funded', 'in_escrow', 'accepted'].includes(order.state));
  const delivering = providerOrders.filter(order => order.state === 'in_progress');
  const withBuyer = providerOrders.filter(order => ['delivered', 'buyer_review'].includes(order.state));
  const disputed = providerOrders.filter(order => order.state === 'disputed');
  return <div className="space-y-6" data-id="element-9189" data-mp="3eseinc0ftubn">
      <PageHeader eyebrow="Service provider" title={providerProfile.headline} description={`@${providerProfile.handle} · ${providerProfile.category} → ${providerProfile.subcategory} · ${providerProfile.responseTime}`} actions={<Badge tone="ok" data-id="element-9191" data-mp="z1ikf0abifnc">Identity verified</Badge>} data-id="element-9190" data-mp="3eviwz27ma6g1" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Provider sections" data-id="element-9192" data-mp="z44ub6kol78q" />

      {tab === 'Today' && <AttentionBoard emptyNote="Every order is either delivered or with the buyer. Nothing needs you right now." items={[...disputed.map(order => ({
      id: order.id,
      title: `Respond to the dispute on ${order.reference}`,
      detail: 'The buyer has disputed this order. Escrow is holding the value until it is resolved — respond with what you delivered and when.',
      meta: `${order.title} · ${naira(order.amountNaira)}`,
      urgency: 'now' as const
    })), ...awaiting.map(order => ({
      id: order.id,
      title: `Start ${order.title}`,
      detail: 'Funded and waiting for you to begin. The clock the buyer sees is already running.',
      meta: `${order.reference} · ${order.counterparty} · ${order.dueLabel}`,
      urgency: 'now' as const
    })), ...delivering.map(order => ({
      id: order.id,
      title: `Deliver ${order.title}`,
      detail: 'In progress. Delivering moves it to the buyer for approval.',
      meta: `${order.reference} · ${order.dueLabel}`,
      urgency: 'soon' as const
    })), ...withBuyer.map(order => ({
      id: order.id,
      title: `${order.reference} is with the buyer`,
      detail: 'Nothing for you to do. It completes on their approval, or automatically once the review window closes.',
      meta: `${naira(order.amountNaira)} held in escrow`,
      urgency: 'watch' as const
    }))]} summary={[{
      label: 'Withdrawable',
      value: naira(providerEarnings.withdrawableNaira),
      note: 'Settled and clear of the refund window.'
    }, {
      label: 'Held in escrow',
      value: naira(providerEarnings.escrowNaira),
      note: 'Committed to work in progress. Not yours until delivery is accepted.'
    }, {
      label: 'Response time',
      value: providerProfile.responseTime,
      note: `Across ${providerProfile.completedOrders} completed orders.`
    }]} data-id="element-9193" data-mp="2q6bz9xfr07bg" />}

      {tab === 'Academic services' && <ProviderServiceQueue data-id="element-9194" data-mp="1g1blc76bzauk" />}

      {tab === 'Orders' && <div className="space-y-3" data-id="element-9195" data-mp="1b54gyitl27eq">
          {providerOrders.map(order => {
        const open = order.id === openId;
        return <article key={order.id} className="rounded-card border border-line bg-surface" data-id="element-9196" data-mp="116nlb8jp94mc">
                <button type="button" onClick={() => setOpenId(open ? '' : order.id)} aria-expanded={open} className="flex w-full flex-wrap items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-9197" data-mp="3qvpibt0bx9ba">
                  <div className="min-w-0 flex-1" data-id="element-9198" data-mp="26cx0yy8m2uz1">
                    <p className="text-[14px] font-semibold text-ink" data-id="element-9199" data-mp="26bc3azr02vwy">{order.title}</p>
                    <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-9200" data-mp="2bbgimmg69kll">
                      {order.reference} · {order.counterparty} (@{order.counterpartyHandle})
                    </p>
                    <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-9201" data-mp="2l2b9630gb6cu">{order.dueLabel}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3" data-id="element-9202" data-mp="2p697ibhycd4u">
                    <span className="cz-num text-right text-[13px]" data-id="element-9203" data-mp="2u4kfegoosdkf">
                      <span className="block font-semibold text-ink" data-id="element-9204" data-mp="2z2vouqb2km24">
                        {naira(order.amountNaira - order.feeNaira)}
                      </span>
                      <span className="block text-[12px] text-ink-subtle" data-id="element-9205" data-mp="zrkx2fsx959g">
                        after {naira(order.feeNaira)} fee
                      </span>
                    </span>
                    <OrderStateBadge state={order.state} data-id="element-9206" data-mp="wppwvbib47z9" />
                  </div>
                </button>
                {open && <div className="border-t border-line px-5 py-4" data-id="element-9207" data-mp="16mcezsj90kxl">
                    <OrderStateTrack order={order} data-id="element-9208" data-mp="16jgga1zkts83" />
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-3" data-id="element-9209" data-mp="11lonz6v0s43d">
                      {order.state === 'IN_ESCROW' && <Button size="sm" variant="primary" data-id="element-9210" data-mp="1fk4wn3cg04o5">
                          Mark in progress
                        </Button>}
                      {order.state === 'DELIVERED' && <Badge tone="warn" data-id="element-9211" data-mp="1kdhk3a5dy5fb">Awaiting buyer approval — funds still in escrow</Badge>}
                      <Button size="sm" variant="secondary" data-id="element-9212" data-mp="1z9p3w9cvt4u2">
                        Message buyer
                      </Button>
                    </div>
                  </div>}
              </article>;
      })}
        </div>}

      {tab === 'Services' && <Panel title="Packages" description="Pricing, delivery time and availability." bodyClassName="p-0" data-id="element-9213" data-mp="2hz294a0jw8t7">
          <ul className="divide-y divide-line" data-id="element-9214" data-mp="23xy7zw12pml5">
            {providerPackages.map(item => <li key={item.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-9215" data-mp="28x1d1gh1zst4">
                <div className="min-w-0 flex-1" data-id="element-9216" data-mp="1u8y4q0fjg515">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-9217" data-mp="1u73lqrntumzp">{item.name}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-9218" data-mp="1z1hm78hcjkb8">
                    {item.sessionMinutes ? `${item.sessionMinutes} minute session` : `Delivery in ${item.deliveryDays} days`}{' '}
                    · {item.ordersThisMonth} orders this month
                  </p>
                </div>
                <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-9219" data-mp="1g9yguuzunu61">
                  {naira(item.priceNaira)}
                </span>
                <Badge tone={item.active ? 'ok' : 'neutral'} data-id="element-9220" data-mp="1vqnciz373qiy">
                  {item.active ? 'Listed' : 'Paused'}
                </Badge>
              </li>)}
          </ul>
          <div className="border-t border-line px-5 py-3" data-id="element-9221" data-mp="1mpgqkahs1nib">
            <Button size="sm" variant="secondary" data-id="element-9222" data-mp="1hrxcbal2xaqd">
              Add a package
            </Button>
          </div>
        </Panel>}

      {tab === 'Earnings' && <div className="space-y-6" data-id="element-9223" data-mp="1cuf3rlaz4xiw">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-9224" data-mp="181calea3zq38">
            {[{
          label: 'Pending',
          value: providerEarnings.pendingNaira,
          note: 'Unsettled orders'
        }, {
          label: 'In escrow',
          value: providerEarnings.escrowNaira,
          note: 'Held against open orders'
        }, {
          label: 'Available',
          value: providerEarnings.availableNaira,
          note: 'Completed, inside refund window'
        }, {
          label: 'Withdrawable',
          value: providerEarnings.withdrawableNaira,
          note: 'Refund window cleared'
        }].map((card, index) => <div key={card.label} className={`rounded-card border bg-surface px-5 py-4 ${index === 3 ? 'border-brand-700' : 'border-line'}`} data-id="element-9225" data-mp="f1feg92pkxlf" data-mpi={index}>
                <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-9226" data-mp="twvdksi5bjoo">
                  {card.label}
                </p>
                <p className="cz-num mt-2 text-2xl font-semibold text-ink" data-id="element-9227" data-mp="3sfsj8uz3vwy0">{naira(card.value)}</p>
                <p className="mt-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-9228" data-mp="17xvoqhujowg">{card.note}</p>
              </div>)}
          </div>
          <Panel title="Settlement rules" data-id="element-9229" data-mp="ax7qie42rlo9">
            <ul className="space-y-2 text-[13px] leading-6 text-ink-muted" data-id="element-9230" data-mp="ay9aj832jii4">
              <li data-id="element-9231" data-mp="39o9ikusk0uz5">· Campuzon fee: {Math.round(providerEarnings.feeRate * 100)}% of order value, configurable per category.</li>
              <li data-id="element-9232" data-mp="3ekzztlg3hwm5">· Refund window: {providerEarnings.refundWindowDays} days after completion before funds become withdrawable.</li>
              <li data-id="element-9233" data-mp="2galjo5zi4s03">· Escrow is released only on buyer approval, automatic completion, or a resolved dispute.</li>
              <li data-id="element-9234" data-mp="2q6f4sdradtth">· Payout requires identity verification and passing risk checks.</li>
            </ul>
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-3" data-id="element-9235" data-mp="2u7qpc4vazout">
              <Button variant="primary" disabled={!isLive('payouts')} data-id="element-9236" data-mp="1xhzug4j3nzoh">
                Request payout
              </Button>
              <FeatureGate featureKey="payouts" inline data-id="element-9237" data-mp="22g1f2tw6c722" />
            </div>
            <div className="mt-4" data-id="element-9238" data-mp="26io8v0ceo21w">
              <FeatureGate featureKey="payouts" data-id="element-9239" data-mp="26hd2qtvhmi7m" />
            </div>
          </Panel>
        </div>}

      {tab === 'Reputation' && <div className="grid gap-6 sm:grid-cols-2" data-id="element-9240" data-mp="2bb8wtl8mrkxi">
          <Panel title="Marketplace reputation" data-id="element-9241" data-mp="1eq728cbuuiht">
            <p className="cz-num text-3xl font-semibold text-ink" data-id="element-9242" data-mp="1epff7pwbpt35">
              {providerProfile.rating.toFixed(1)}
            </p>
            <p className="cz-num mt-1 text-[13px] text-ink-muted" data-id="element-9243" data-mp="1b9uc8c0pqjxr">
              {providerProfile.reviewCount} reviews across {providerProfile.completedOrders} completed
              orders
            </p>
            <p className="mt-3 border-t border-line pt-3 text-[13px] leading-6 text-ink-muted" data-id="element-9244" data-mp="127uwdn1fplwe">
              This score covers services only. Your housing, knowledge and community standing are
              tracked separately and never merged into one platform-wide number.
            </p>
          </Panel>
          <Panel title="What counts" data-id="element-9245" data-mp="nioz44m5q5r5">
            <ul className="space-y-2 text-[13px] leading-6 text-ink-muted" data-id="element-9246" data-mp="nhndx7bfg2vp">
              <li data-id="element-9247" data-mp="il6ng6gr9cfg">· Completed orders and on-time delivery</li>
              <li data-id="element-9248" data-mp="ejle2v3fjga0">· Buyer reviews and ratings</li>
              <li data-id="element-9249" data-mp="ekwimx4p8s26">· Identity verification status</li>
              <li data-id="element-9250" data-mp="9k8inlyht0vt">· Dispute history and outcomes</li>
              <li data-id="element-9251" data-mp="9m1ox0571j3a">· Response time to enquiries</li>
            </ul>
          </Panel>
        </div>}
    </div>;
}